import { useTheme } from 'components/ThemeProvider';
import { toJsxRuntime } from 'hast-util-to-jsx-runtime';
import { Fragment, jsx, jsxs } from 'react/jsx-runtime';
import { refractor } from 'refractor/core';
import yaml from 'refractor/yaml';
import { classes } from 'utils/style';
import styles from './CodeBlock.module.css';

// Register only the languages the site shows, so the bundle stays small.
refractor.register(yaml);

function highlight(code, lang) {
  if (!refractor.registered(lang)) return code;
  return toJsxRuntime(refractor.highlight(code, lang), { Fragment, jsx, jsxs });
}

// A captured session: `$ ` lines are what was typed, everything else is output.
function session(code) {
  return code.split('\n').map((line, index) => (
    <Fragment key={index}>
      {index > 0 && '\n'}
      {line.startsWith('$ ') ? (
        <>
          <span className={styles.prompt}>$</span>{' '}
          <span className={styles.command}>{line.slice(2)}</span>
        </>
      ) : (
        <span className={styles.output}>{line}</span>
      )}
    </Fragment>
  ));
}

/**
 * A fenced code or captured terminal session.
 *
 * Pass plain text as `children` for the common case, or pre-marked children
 * (spans with your own classes) when you want emphasis. Plain text is
 * highlighted by `lang`, or split into prompt and output for a terminal.
 *
 * `lineNumbers` needs a plain string child to count lines, so it's ignored
 * for pre-marked children.
 */
export function CodeBlock({
  children,
  variant = 'code',
  title,
  lang,
  lineNumbers,
  className,
  ...rest
}) {
  const theme = useTheme();
  const terminal = variant === 'terminal';
  const code = typeof children === 'string' ? children.replace(/\n$/, '') : children;
  const text = typeof code === 'string';
  const numbers = lineNumbers && text ? code.split('\n').length : null;
  const rendered = !text ? code : terminal ? session(code) : highlight(code, lang);

  return (
    <div
      className={classes(styles.block, className)}
      data-theme={theme.themeId}
      data-variant={variant}
      data-lang={lang || undefined}
      {...rest}
    >
      {terminal && (
        <div className={styles.bar}>
          <span className={styles.dots} aria-hidden />
          {!!title && <span className={styles.title}>{title}</span>}
        </div>
      )}
      {/* "text" is the absence of a language, so there is nothing to label. */}
      {!!lang && lang !== 'text' && <span className={styles.lang}>{lang}</span>}
      <pre className={styles.pre}>
        {!!numbers && (
          <span className={styles.gutter} aria-hidden>
            {Array.from({ length: numbers }, (_, index) => index + 1).join('\n')}
          </span>
        )}
        <code className={styles.code}>{rendered}</code>
      </pre>
    </div>
  );
}
